/**
 * route_pages 组件解析器
 *
 * 后端 route_pages 返回的 component 字段是 views/ 下的相对路径：
 *   "dashboard/index"                           → @/views/dashboard/index.vue
 *   "members/member-management/member-list/index" → @/views/members/.../index.vue
 *
 * 使用 Vite 的 import.meta.glob 预收集所有视图模块，构建时静态分析。
 */

const viewModules = import.meta.glob('@/views/**/*.vue');

/**
 * 根据后端 component 值解析 Vue 组件
 * @param {string} componentPath - 后端返回的 component 字段
 * @returns {(() => Promise<*>)|null}
 */
export function resolveComponent(componentPath) {
  if (!componentPath) return null;
  const targetSuffix = `/views/${componentPath}.vue`;
  for (const [key, loader] of Object.entries(viewModules)) {
    if (key.replace(/\\/g, '/').endsWith(targetSuffix)) {
      return loader;
    }
  }
  console.warn(`[routePages] 未找到组件: ${componentPath}`);
  return null;
}

/**
 * 根据 route_pages 列表生成 vue-router 路由配置
 * @param {Array<{ path: string, component: string, name?: string, icon?: string }>} routePages
 * @returns {Array<{ path: string, name: string, component: (() => Promise<*>), meta: object }>}
 */
export function generateRoutesFromRoutePages(routePages) {
  if (!Array.isArray(routePages) || !routePages.length) return [];

  return routePages
    .map(item => {
      const component = resolveComponent(item.component);
      if (!component) return null;

      return {
        path: item.path?.replace(/^\/+/, '/') || '/',
        name: item.name || item.component?.replace(/\//g, '_'),
        component,
        meta: {
          title: item.name,
          icon: item.icon,
          _fromBackend: true,
        },
      };
    })
    .filter(Boolean);
}

/**
 * 注册动态路由到 Layout 下
 * @param {import('vue-router').Router} router
 * @param {Array} routes
 */
export function addDynamicRoutesToLayout(router, routes) {
  routes.forEach(route => {
    try {
      router.addRoute('Layout', route);
    } catch (e) {
      console.warn(`[routePages] 注册路由失败: ${route.path}`, e);
    }
  });
}

/**
 * 获取 route_pages 中第一条有效路径（用于 Layout 重定向）
 * @param {Array} routePages
 * @returns {string|null}
 */
export function getFirstRoutePagePath(routePages) {
  if (!Array.isArray(routePages) || !routePages.length) return null;
  return routePages[0].path;
}
